import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { message } from 'antd';
import { UserSessionResult } from '@wisory-international-ab/login-frontend';
import { getToken, refreshAccessToken, setToken } from '@common/services';
import { logError } from '../services/monitor';
import { getAppConfigurations } from './environment-variables';

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  skipGlobalErrorMessage?: boolean;
  skipInterceptor?: boolean;
  apiGroup?: 'main' | 'profile';
}

declare module 'axios' {
  export interface AxiosInstance {
    request<T = any>(config: CustomAxiosRequestConfig): Promise<T>;
    get<T = any>(url: string, config?: CustomAxiosRequestConfig): Promise<T>;
    delete<T = any>(url: string, config?: CustomAxiosRequestConfig): Promise<T>;
    head<T = any>(url: string, config?: CustomAxiosRequestConfig): Promise<T>;
    post<T = any>(url: string, data?: any, config?: CustomAxiosRequestConfig): Promise<T>;
    put<T = any>(url: string, data?: any, config?: CustomAxiosRequestConfig): Promise<T>;
    patch<T = any>(url: string, data?: any, config?: CustomAxiosRequestConfig): Promise<T>;
  }
}

const { mainApiEndpoint, profileApiEndpoint } = getAppConfigurations();

export const request = axios.create({
  baseURL: mainApiEndpoint,
});

request.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error?.config;
    const response = error?.response;
    const status = response?.status;
    const url = originalRequest?.url;
    const method = originalRequest?.method;

    const reloadWhenTokenIsExpired = () => {
      message.error('Your session is lost, redirect to login...');
      setTimeout(() => {
        // window.location.reload();
      }, 2000);
    };

    // ============ NETWORK / HTML ERROR CASE ============
    if (!response) {
      // Axios had no response object — could be CORS, network, or HTML error page
      logError('Axios network/HTML error', {
        url,
        method,
        message: error?.message,
        code: error?.code,
        name: error?.name,
        stack: error?.stack,
        config: {
          baseURL: originalRequest?.baseURL,
          timeout: originalRequest?.timeout,
          headers: originalRequest?.headers,
        },
      });

      if (!error.config?.skipGlobalErrorMessage) {
        message.error(
          'Network error or invalid server response. Please try again or contact support.'
        );
      }

      return Promise.reject(error);
    }

    // ============ 401 TOKEN EXPIRED ============
    const isTokenExpired = status === 401 && !originalRequest?._retry;
    if (isTokenExpired) {
      const userSession = getToken();

      if (userSession && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const newUserSession = await refreshAccessToken(userSession.refreshToken);
          setToken(newUserSession as UserSessionResult);
          return request(originalRequest);
        } catch (e) {
          return Promise.reject(e);
        }
      } else if (!error.config?.skipInterceptor) {
        reloadWhenTokenIsExpired();
      }

      return Promise.reject(error);
    }

    // ============ ANY OTHER 401 ============
    if (status === 401) {
      reloadWhenTokenIsExpired();
    }

    // ============ 403 FORBIDDEN LOGGING ============
    if (status === 403) {
      const logContext = {
        message: '403 Forbidden response',
        url,
        method,
        requestData: originalRequest?.data ?? null,
        responseText: response?.data ?? null,
        headers: originalRequest?.headers ?? null,
      };

      logError('Axios 403 Forbidden', logContext);

      if (!error.config?.skipGlobalErrorMessage) {
        message.error('You do not have permission to perform this action (403).');
      }

      return Promise.reject(response?.data);
    }

    // ============ OTHER ERRORS ============
    const { data } = response;

    if (!error.config?.skipGlobalErrorMessage) {
      message.error(
        data && data.errorMessage
          ? data.errorMessage
          : 'There is an error, please contact our administrator for help.'
      );
    }

    // log all other unexpected HTTP errors too
    logError('Axios unexpected HTTP error', {
      url,
      method,
      status,
      responseData: response?.data ?? null,
    });

    return Promise.reject(response?.data);
  }
);

export const applyInterceptor = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.request.use(
    (config: any) => {
      const token = getToken();
      const { headers, apiGroup } = config as CustomAxiosRequestConfig;
      const baseURL = apiGroup === 'profile' ? profileApiEndpoint : mainApiEndpoint;
      const customConfig = {
        ...config,
        baseURL,
      };

      if (!token) {
        return customConfig;
      }

      return {
        ...customConfig,
        headers: {
          ...headers,
          Authorization: `Bearer ${token.accessToken}`,
        },
      };
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

applyInterceptor(request);
